import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ScrollText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { classifyErr } from "@/lib/admin";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const PAGE_SIZES = [10, 20, 50];

const METHOD_COLOR = {
  POST: "bg-emerald-100 text-emerald-700",
  PATCH: "bg-amber-100 text-amber-700",
  PUT: "bg-amber-100 text-amber-700",
  DELETE: "bg-red-100 text-red-700",
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [entityTypes, setEntityTypes] = useState([]);
  const [users, setUsers] = useState([]);

  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [actor, setActor] = useState("");
  const [method, setMethod] = useState("");
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [et, u] = await Promise.all([
          api.get("/audit-log/entity-types"),
          api.get("/users", { params: { page: 1, page_size: 100 } }),
        ]);
        setEntityTypes(et.data.items);
        setUsers(u.data.items);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const userById = Object.fromEntries(users.map((u) => [u.id, u]));

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: pageSize };
      if (entityType) params.entity_type = entityType;
      if (entityId) params.entity_id = entityId;
      if (actor) params.actor_user_id = actor;
      if (method) params.method = method;
      const { data } = await api.get("/audit-log", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(classifyErr(e));
    } finally {
      setBusy(false);
    }
  }, [page, pageSize, entityType, entityId, actor, method]);

  useEffect(() => { load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-5" data-testid="admin-audit-log">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Settings" }, { label: "Audit log" }]}
        title="Audit log"
        subtitle="Every change made by users — filter by user, item or time."
        favorite={{ key: "admin.audit", type: "screen", label: "Audit log", route: "/admin/audit" }}
        testid="auditlog-page-header"
      />


      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Entity type</Label>
            <Select value={entityType || "all"} onValueChange={(v) => { setEntityType(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-10 mt-1" data-testid="audit-entity-type"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {entityTypes.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Entity id</Label>
            <Input value={entityId} onChange={(e) => { setEntityId(e.target.value); setPage(1); }} className="h-10 mt-1" placeholder="uuid…" data-testid="audit-entity-id" />
          </div>
          <div>
            <Label className="text-xs">Actor</Label>
            <Select value={actor || "all"} onValueChange={(v) => { setActor(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-10 mt-1" data-testid="audit-actor"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={method || "all"} onValueChange={(v) => { setMethod(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-10 mt-1" data-testid="audit-method"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {["POST", "PATCH", "PUT", "DELETE"].map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={() => { setEntityType(""); setEntityId(""); setActor(""); setMethod(""); setPage(1); }} className="h-10 w-full">Reset</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-neutral-600 w-8"></th>
                <th className="px-3 py-2 text-left font-medium text-neutral-600">When</th>
                <th className="px-3 py-2 text-left font-medium text-neutral-600">Actor</th>
                <th className="px-3 py-2 text-left font-medium text-neutral-600">Method</th>
                <th className="px-3 py-2 text-left font-medium text-neutral-600">Path</th>
                <th className="px-3 py-2 text-left font-medium text-neutral-600">Entity</th>
                <th className="px-3 py-2 text-left font-medium text-neutral-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {busy && (<tr><td colSpan={7} className="text-center text-neutral-500 py-6">Loading…</td></tr>)}
              {!busy && items.length === 0 && (<tr><td colSpan={7} className="text-center text-neutral-500 py-6">No audit entries match.</td></tr>)}
              {!busy && items.map((row) => (
                <AuditRow
                  key={row.id}
                  row={row}
                  expanded={!!expanded[row.id]}
                  onToggle={() => setExpanded((x) => ({ ...x, [row.id]: !x[row.id] }))}
                  userById={userById}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100 text-sm">
          <div className="text-neutral-500" data-testid="audit-total">{total} entries</div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-neutral-500">Rows:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-[76px]"><SelectValue /></SelectTrigger>
                <SelectContent>{PAGE_SIZES.map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <span className="px-2 text-neutral-600">{page} / {pageCount}</span>
              <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function AuditRow({ row, expanded, onToggle, userById }) {
  return (
    <>
      <tr
        className="hover:bg-neutral-50 cursor-pointer"
        onClick={onToggle}
        data-testid={`audit-row-${row.id}`}
      >
        <td className="px-3 py-2 text-neutral-400">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="px-3 py-2 text-xs text-neutral-600 whitespace-nowrap">
          {new Date(row.ts).toLocaleString()}
        </td>
        <td className="px-3 py-2 text-xs">
          <div className="font-medium text-neutral-800">{userById[row.actor_user_id]?.name || "system"}</div>
          <div className="text-neutral-400">{row.actor_user_id ? row.actor_user_id.slice(0, 8) : "—"}</div>
        </td>
        <td className="px-3 py-2">
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${METHOD_COLOR[row.method] || "bg-neutral-100 text-neutral-700"}`}>
            {row.method}
          </span>
        </td>
        <td className="px-3 py-2 font-mono text-xs">{row.path}</td>
        <td className="px-3 py-2 text-xs">
          <div>{row.entity_type || "—"}</div>
          {row.entity_id && <div className="text-neutral-400">{row.entity_id.slice(0, 8)}</div>}
        </td>
        <td className="px-3 py-2">
          {row.success ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{row.response_status}</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{row.response_status}</Badge>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-neutral-50" data-testid={`audit-row-detail-${row.id}`}>
          <td colSpan={7} className="px-6 py-4">
            <FieldDiff request={row.request_body} before={row.before} after={row.after} />
            <details className="mt-3">
              <summary className="text-[11px] uppercase tracking-widest text-neutral-500 cursor-pointer select-none">
                Raw JSON
              </summary>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-2">
                <DiffPanel title="Request body" data={row.request_body} />
                <DiffPanel title="After (response body)" data={row.after} />
              </div>
            </details>
            <div className="text-[11px] text-neutral-400 mt-2">
              IP {row.ip || "—"} · UA {row.user_agent ? row.user_agent.slice(0, 60) + "…" : "—"}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Compact per-field diff renderer. Shows only fields present in the
// request_body (the caller's intent) with the "after" side-by-side, so a
// zone/role/manager change reads like: `zone_id: gj → rj` instead of two
// giant JSON blobs. (UAT RC5b)
function FieldDiff({ request, before, after }) {
  const req = (request && typeof request === "object" && !Array.isArray(request)) ? request : null;
  if (!req) return <div className="text-xs text-neutral-500">No structured change to diff.</div>;
  const keys = Object.keys(req);
  if (keys.length === 0) return <div className="text-xs text-neutral-500">No fields changed in this request.</div>;
  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) return `[${v.map(fmt).join(", ")}]`;
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 px-3 py-2 border-b border-neutral-100">
        Field-level diff
      </div>
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-[11px] uppercase tracking-widest text-neutral-500">
          <tr>
            <th className="px-3 py-1.5 text-left w-1/4">Field</th>
            <th className="px-3 py-1.5 text-left">Requested</th>
            <th className="px-3 py-1.5 text-left">After</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {keys.map((k) => {
            // Phase 25E1 · Item 3b — workflow_permissions gets a bespoke
            // diff chip renderer (before→after per-code chips) so the raw
            // dict dump never overflows. The actual audit-log key emitted by
            // PUT /api/users/{id}/workflow-permissions is `permission_codes`;
            // legacy keys are `workflow_permissions` / `permissions`.
            if (k === "workflow_permissions" || k === "permissions" ||
                k === "permission_codes") {
              return (
                <tr key={k} data-testid={`audit-diff-row-${k}`}>
                  <td className="px-3 py-1.5 text-xs font-mono text-neutral-700 align-top">{k}</td>
                  <td colSpan={2} className="px-3 py-1.5">
                    <PermissionDiff
                      request={req[k]}
                      before={(before || {})[k]}
                      after={(after || {})[k]} />
                  </td>
                </tr>
              );
            }
            const rv = fmt(req[k]);
            const av = fmt(after && typeof after === "object" ? after[k] : undefined);
            const changed = rv !== av && !(rv === "—" && av === "—");
            return (
              <tr key={k} data-testid={`audit-diff-row-${k}`}>
                <td className="px-3 py-1.5 text-xs font-mono text-neutral-700">{k}</td>
                <td className={`px-3 py-1.5 text-xs ${changed ? "text-amber-700" : "text-neutral-500"}`}>{rv}</td>
                <td className={`px-3 py-1.5 text-xs ${changed ? "text-emerald-700 font-medium" : "text-neutral-500"}`}>{av}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Phase 25E1 · Item 3b — permission diff chip renderer. Renders each
// permission code as a small `code: on → off` / `off → on` chip. When
// > 10 permissions changed, collapses into a "N permissions changed"
// summary with an expand toggle.
function PermissionDiff({ request, before, after }) {
  const [expanded, setExpanded] = React.useState(false);
  const req = Array.isArray(request) ? request : [];
  // Prefer explicit `before` (audit_log persists both before + after) — falls
  // back to `request` if the audit row didn't capture a before-image.
  const beforeArr = Array.isArray(before) ? before : req;
  const afterArr  = Array.isArray(after)  ? after  : beforeArr;
  const beforeSet = new Set(beforeArr);
  const afterSet  = new Set(afterArr);
  const codes = Array.from(new Set([...beforeSet, ...afterSet])).sort();
  const changed = codes.filter((c) => beforeSet.has(c) !== afterSet.has(c));
  const kept    = codes.filter((c) => beforeSet.has(c) && afterSet.has(c));
  const showAll = expanded || changed.length + kept.length <= 10;
  return (
    <div className="space-y-2" data-testid="audit-perm-diff">
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-neutral-500">
          {changed.length} change{changed.length === 1 ? "" : "s"}
          {kept.length > 0 && ` · ${kept.length} unchanged`}
        </span>
        {changed.length + kept.length > 10 && (
          <button className="underline text-neutral-600" onClick={() => setExpanded((v) => !v)}
            data-testid="audit-perm-diff-toggle">
            {expanded ? "collapse" : "expand"}
          </button>
        )}
      </div>
      {changed.length === 0 ? (
        <div className="text-xs text-neutral-500">No permission changes.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="audit-perm-diff-changed">
          {changed.map((code) => {
            const on = afterSet.has(code);
            return (
              <span key={code} data-testid={`audit-perm-chip-${code}`}
                className={
                  "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-mono " +
                  (on ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-rose-50 text-rose-700 border-rose-200")
                }>
                {code}: <span className="font-semibold">{on ? "off → on" : "on → off"}</span>
              </span>
            );
          })}
        </div>
      )}
      {showAll && kept.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {kept.map((code) => (
            <span key={code}
              className="inline-flex items-center rounded-md border border-neutral-200 bg-neutral-50 text-neutral-500 px-2 py-0.5 text-[11px] font-mono">
              {code}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


function DiffPanel({ title, data }) {  const pretty = data ? JSON.stringify(data, null, 2) : "—";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">{title}</div>
      <pre className="bg-neutral-900 text-emerald-100 text-[11px] font-mono p-3 rounded-lg overflow-x-auto max-h-64">
        {pretty}
      </pre>
    </div>
  );
}
